import type { SummaryCreditRatingSlice } from '../api/chartDataTypes';
import { SnapshotValueSelector, submitGuidance } from './SnapshotValueSelector';

const getSliceKey = (slice: SummaryCreditRatingSlice) => slice.label;

export const SUMMARY_CREDIT_RATING_SUBMIT_GUIDANCE = submitGuidance('credit rating');

type SummaryCreditRatingSelectorProps = {
    /** Snapshot slices from the summary_credit_rating payload. Order is
     *  irrelevant — the selector sorts by `label` ascending internally. */
    slices: SummaryCreditRatingSlice[];
    /** Prefix for the `<select>` id so multiple snapshot selectors on a page
     *  never collide. */
    idPrefix: string;
    /** Called only when the user presses Submit after picking a credit rating.
     *  Selecting the same option again and re-submitting still fires so the
     *  parent can re-announce. */
    onSelectedSliceChange: (slice: SummaryCreditRatingSlice) => void;
};

/**
 * Thin `SnapshotValueSelector` wrapper for the Summary Credit Rating chart's
 * slice picker — see `SnapshotValueSelector` for the shared Submit-gating
 * behavior (WCAG 2.2 3.2.2 On Input).
 */
export function SummaryCreditRatingSelector({
    slices,
    idPrefix,
    onSelectedSliceChange,
}: SummaryCreditRatingSelectorProps) {
    return (
        <SnapshotValueSelector<SummaryCreditRatingSlice>
            values={slices}
            getKey={getSliceKey}
            idPrefix={idPrefix}
            fieldLabel='Data Series'
            placeholder='Select data series'
            selectedAriaLabel='Data series'
            hint='Select a credit rating, then press Submit to display its percentage.'
            disabledSubmitAriaLabel='Submit — select a credit rating to continue'
            readySubmitAriaLabel={(label) => `Submit and display percentage for ${label}`}
            onSubmit={onSelectedSliceChange}
        />
    );
}
